import { BaseWidgetView } from '../../../widget/BaseWidgetView';
import { htmlDecode } from '../../../utils/escape.utils';
import {
  EndpointModuleStatus,
  GetEndpointModuleStatusResponse,
  Status,
} from '@nirvana/shared/dtos/types/widgets/common';
import { DisplayMode, TemplateState } from '../../../types/common/types';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../../types/widget/types';
import { TableHeader, TableItem } from '../../../ui/gzTable/types';
import { GzTableWebComponent } from '../../../index';

export class EndpointModulesStatusDetailsView extends BaseWidgetView {
  public shadow: any;
  protected translations: any;
  private readonly $style: any;
  private colors: {
    enabled: string;
    disabled: string;
    notInstalled: string;
  };
  private errorColor: string;
  private columnHeaders: { enabled: string; disabled: string; notInstalled: string };

  private setChartColors() {
    this.colors = {
      enabled: '--blue-chart-050',
      disabled: '--blue-chart-400',
      notInstalled: '--purple-chart-050',
    };
    this.errorColor = '--grey-layout-203-dark-theme-disabled-text';
  }

  private getColorMap(resultStatus: Status): { enabled: string; disabled: string; notInstalled: string } {
    switch (resultStatus) {
      case Status.ACTIVE:
        return this.colors;
      case Status.EMPTY_STATE:
      default:
        return {
          enabled: this.errorColor,
          disabled: this.errorColor,
          notInstalled: this.errorColor,
        };
    }
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getEndpointModulesStatusDetailsWidgetStrings();
  }

  setTranslatedStrings() {
    const { title, options } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;
    this.columnHeaders = {
      enabled: options.enabled,
      disabled: options.disabled,
      notInstalled: options.notInstalled,
    };
  }

  getDomElements() {
    super.getDomElements();
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$widgetTitleString = this.shadow.querySelector('#endpointModulesTitle');
    this.dom.$style = getComputedStyle(document.body);
    this.dom.$tableLeft = this.shadow.querySelector('#modulesDetailsLeft') as GzTableWebComponent;
    this.dom.$tableRight = this.shadow.querySelector('#modulesDetailsRight') as GzTableWebComponent;
    this.dom.$legend = this.shadow.querySelector('.legend');
    this.getTranslations();
    this.setChartColors();
    this.setTranslatedStrings();
    this.setErrorTooltip();
  }

  buildSuccessView(response: BackendResponseWithMeta<GetEndpointModuleStatusResponse>) {
    this.resetTemplateState(this.$wrapper);
    if (response[DASHBOARD_METADATA_KEY].displayMode !== DisplayMode.PDF) {
      // If we want to add this outside of PDF, we need to fix the legend on the widget
      throw new Error('Trying to use widget outside PDF');
    }

    const result = response.widgetResult;

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.buildEmptyTemplate();
      return;
    }

    if (result.data && result.status === Status.ACTIVE) {
      const formattedData = this.formatData(result.data);
      const { dataLeft, dataRight } = this.splitDataForTables(formattedData);
      const header = this.getTableHeader(Status.ACTIVE);
      this.renderTable(this.dom.$tableLeft, dataLeft, header);
      this.renderTable(this.dom.$tableRight, dataRight, header);
      this.renderLegend(Status.ACTIVE);
      return;
    }

    this.buildErrorView();
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildEmptyTemplate() {
    const emptyData: TableItem[] = Array.from({ length: 7 }, () => ({
      label: '...',
      value: ['-', '-', '-'],
    }));

    const header = this.getTableHeader(Status.EMPTY_STATE);
    this.renderTable(this.dom.$tableLeft, emptyData, header);
    this.renderTable(this.dom.$tableRight, emptyData, header);
    this.renderLegend(Status.EMPTY_STATE);
  }

  private splitDataForTables(data: Array<TableItem>) {
    const midPoint = Math.ceil(data.length / 2);

    const dataLeft = data.slice(0, midPoint);
    const dataRight = data.slice(midPoint);
    return { dataLeft, dataRight };
  }

  private formatData(data: Array<EndpointModuleStatus>): Array<TableItem> {
    return data.map((element: EndpointModuleStatus) => {
      return {
        label: htmlDecode(element.name),
        value: this.orderColumns(element).map(value => value.toString()),
      };
    });
  }

  private getTableHeader(resultStatus: Status): TableHeader {
    return {
      header: ['', ...this.orderColumns(this.columnHeaders)],
      color: ['', ...this.orderColumns(this.getColorMap(resultStatus))],
      hideTextLabel: true,
    };
  }

  private orderColumns(data: { enabled: any; disabled: any; notInstalled: any }): Array<any> {
    return [data.enabled, data.disabled, data.notInstalled];
  }

  private renderTable(tableElement: GzTableWebComponent, data: Array<TableItem>, header: TableHeader) {
    tableElement.data = data;
    tableElement.head = header;
  }

  private renderLegend(resultStatus: Status): void {
    const labels = this.orderColumns(this.columnHeaders);
    const colors = this.orderColumns(this.getColorMap(resultStatus));
    const legendItems = labels.map((label, index) => ({ label, color: colors[index] }));
    legendItems.forEach(item => {
      const legendElement = document.createElement('div');
      legendElement.classList.add('legend-element');
      const colorElement = document.createElement('span');
      colorElement.classList.add('color-element');
      const labelElement = document.createElement('span');
      labelElement.classList.add('label-element');

      colorElement.style.backgroundColor = `var(${item.color})`;
      labelElement.textContent = item.label;

      legendElement.appendChild(colorElement);
      legendElement.appendChild(labelElement);
      this.dom.$legend.appendChild(legendElement);
    });
  }
}
